import { Badge, HStack, Text, Tooltip } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  InvoicingPlanExportBasicDocument,
  type InvoicingPlanExportBasicQuery,
  type InvoicingPlanExportBasicQueryVariables,
  InvoicingPlanExportDetailedDocument,
  type InvoicingPlanExportDetailedQuery,
  type InvoicingPlanExportDetailedQueryVariables,
  ProjectBillingDocumentTypeEnum,
  ProjectBillingItemStatusEnum,
  type ProjectBillingRowFragment,
  type ProjectBillingSubRowFragment,
  ProjectBillingsFilterOptionsDocument,
  type ProjectBillingsFilterOptionsQuery,
  type ProjectBillingsFilterOptionsQueryVariables,
  ProjectBillingsListDocument,
  type ProjectBillingsListQuery,
  type ProjectBillingsListQueryVariables,
  ProjectBillingsOrderByColumnEnum,
  ProjectBillingsSummaryDocument,
  type ProjectBillingsSummaryQuery,
  type ProjectBillingsSummaryQueryVariables,
  ProjectBillingsWhereColumn,
  ProjectBillingTypeEnum,
  type SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  BrandLogo,
  ButtonLink,
  formatDate,
  type IOption,
  Link,
  UserTooltip,
} from "@src/components/ui-kit";
import { ProjectCategorySelectBottomExtraContent } from "@src/components/ui-kit/ProjectCategorySelect";
import { getMonths } from "@src/config/constants";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import type { PipedriveStore } from "@src/stores/pipedrive";
import { commonSearchParams } from "@src/utils/apolloHelpers";
import { getPaymentStatusFilterOptions } from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { now, toApiDate } from "@src/utils/dates";
import { currency } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import type { DateRange } from "@src/utils/types";
import { endOfMonth, startOfMonth } from "date-fns";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import {
  DeliveredStatusRenderer,
  InvoiceNoRenderer,
  StackedAmountRenderer,
} from "../components/columns";

type ProjectBillingGroupRow = Record<string, unknown> & {
  id: string;
  rowType: "group";
  row: ProjectBillingRowFragment;
  __subRows: ProjectBillingTableRow[];
};

type ProjectBillingItemRow = Record<string, unknown> & {
  id: string;
  rowType: "item";
  subrow: ProjectBillingSubRowFragment;
  parent: ProjectBillingRowFragment;
};

type ProjectBillingTableRow = ProjectBillingGroupRow | ProjectBillingItemRow;

const isGroupRow = (
  row: ProjectBillingTableRow,
): row is ProjectBillingGroupRow => row.rowType === "group";

export class InvoicingPlanListStore {
  readonly tableKey = "invoicing-plan-list";

  @observable.ref
  searchTerm = "";

  @observable.ref
  tableData: ProjectBillingTableRow[] = [];

  @observable.ref
  summaryData: NonNullable<
    ProjectBillingsSummaryQuery["projectBillingsSummary"]
  > | null = null;

  @observable.ref
  expenseCategories: NonNullable<
    ProjectBillingsFilterOptionsQuery["expenseBudgetCategories"]
  > = [];

  @observable.ref
  dateRange: DateRange = {
    start: startOfMonth(now()),
    end: endOfMonth(now()),
  };

  @observable.ref
  withPrepayments: BooleanState;

  @observable.ref
  basicExportModalDisclosure = new DisclosureState({
    onOpen: () => {
      this.exportMonth = now().getMonth();
      this.exportYear = now().getFullYear();
    },
  });

  @observable.ref
  exportMonth: number = now().getMonth();

  @observable.ref
  exportYear: number = now().getFullYear();

  pagination = new PaginationState(this.tableKey, {
    onChangePagination: () => {
      this.fetchTableData();
    },
  });

  orderBy = new OrderBy<ProjectBillingsOrderByColumnEnum>();

  where = new Filters<ProjectBillingsWhereColumn>(
    [
      new Filter({
        title: t`Client/Brand`,
        column: ProjectBillingsWhereColumn.BrandId,
        operator: SqlOperator.In,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        title: t`Project`,
        column: ProjectBillingsWhereColumn.ProjectId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Team`,
        column: ProjectBillingsWhereColumn.TeamId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Project Manager`,
        column: ProjectBillingsWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Project Stage`,
        column: ProjectBillingsWhereColumn.ProjectStageId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Status`,
        column: ProjectBillingsWhereColumn.ItemStatus,
        operator: SqlOperator.In,
        options: [
          {
            label: t`Created`,
            value: ProjectBillingItemStatusEnum.Created,
          },
          {
            label: t`Draft`,
            value: ProjectBillingItemStatusEnum.Draft,
          },
          {
            label: t`Failed`,
            value: ProjectBillingItemStatusEnum.Failed,
          },
          {
            label: t`Manually Created`,
            value: ProjectBillingItemStatusEnum.ManuallyCreated,
          },
          {
            label: t`Pending`,
            value: ProjectBillingItemStatusEnum.Pending,
          },
          {
            label: t`Prepayment Used`,
            value: ProjectBillingItemStatusEnum.PrepaymentUsed,
          },
          {
            label: t`Deleted`,
            value: ProjectBillingItemStatusEnum.Deleted,
          },
        ],
      }),
      new Filter({
        title: t`Payment Status`,
        column: ProjectBillingsWhereColumn.PaymentStatus,
        operator: SqlOperator.In,
        options: getPaymentStatusFilterOptions(),
      }),
      new Filter({
        title: t`Date of Supply`,
        column: ProjectBillingsWhereColumn.DateOfSupplyFrom,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Due Date`,
        column: ProjectBillingsWhereColumn.DueDateFrom,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Paid At`,
        column: ProjectBillingsWhereColumn.PaidAtFrom,
        operator: SqlOperator.Between,
        dateRange: true,
        options: [],
      }),
      new Filter({
        title: t`Billing plan type`,
        column: ProjectBillingsWhereColumn.BillingType,
        operator: SqlOperator.In,
        options: [
          {
            label: t`Automatic`,
            value: ProjectBillingTypeEnum.Automatic,
          },
          {
            label: t`Manual`,
            value: ProjectBillingTypeEnum.Manual,
          },
          {
            label: t`None`,
            value: ProjectBillingTypeEnum.None,
          },
        ],
      }),
      new Filter({
        title: t`Project category`,
        column: ProjectBillingsWhereColumn.ProjectCategoryId,
        operator: SqlOperator.In,
        options: [],
        bottomExtraContent: <ProjectCategorySelectBottomExtraContent />,
      }),
      new Filter({
        title: t`Legal entity`,
        column: ProjectBillingsWhereColumn.WorkspaceCompanyId,
        operator: SqlOperator.In,
        options: [],
      }),
      new Filter({
        title: t`Document type`,
        column: ProjectBillingsWhereColumn.Type,
        operator: SqlOperator.In,
        options: [
          {
            value: ProjectBillingDocumentTypeEnum.OutgoingInvoice,
            label: t`Outgoing invoice`,
          },
          {
            value: ProjectBillingDocumentTypeEnum.OutgoingProformaInvoice,
            label: t`Proforma invoice`,
          },
          {
            value: ProjectBillingDocumentTypeEnum.CreditNote,
            label: t`Credit note`,
          },
          {
            value: ProjectBillingDocumentTypeEnum.RequestForInvoicing,
            label: t`Request for invoicing`,
          },
          {
            value: ProjectBillingDocumentTypeEnum.Planned,
            label: t`Planned`,
          },
          {
            value: ProjectBillingDocumentTypeEnum.Prepayment,
            label: t`Prepayment`,
          },
        ],
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummaryData();
      },
      tableKey: this.tableKey,
      orderBy: this.orderBy,
    },
  );

  private fetcherTableData = new FetchHelper<
    ProjectBillingsListQuery,
    ProjectBillingsListQueryVariables
  >(ProjectBillingsListDocument);

  private fetcherSummary = new FetchHelper<
    ProjectBillingsSummaryQuery,
    ProjectBillingsSummaryQueryVariables
  >(ProjectBillingsSummaryDocument);

  private fetcherFilterOptions = new FetchHelper<
    ProjectBillingsFilterOptionsQuery,
    ProjectBillingsFilterOptionsQueryVariables
  >(ProjectBillingsFilterOptionsDocument);

  private fetcherExportBasic = new FetchHelper<
    InvoicingPlanExportBasicQuery,
    InvoicingPlanExportBasicQueryVariables
  >(InvoicingPlanExportBasicDocument);

  private fetcherExportDetailed = new FetchHelper<
    InvoicingPlanExportDetailedQuery,
    InvoicingPlanExportDetailedQueryVariables
  >(InvoicingPlanExportDetailedDocument);

  constructor(appStore: AppStore) {
    makeObservable(this);

    this.fetchFilterOptions();

    const projectStageFilter = this.where.filtersByColumn.get(
      ProjectBillingsWhereColumn.ProjectStageId,
    );

    if (projectStageFilter) {
      projectStageFilter.hidden =
        !appStore.workspaceStore.settings?.pipedriveEnabled;
      this.setProjectStageOptions(appStore.pipedriveStore);
    }

    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.WorkspaceCompanyId)
      ?.setOptions(
        mapToOptions.workspaceCompanies(appStore.workspaceStore.companies),
      );

    this.where.fromURLSearchParam(Router.query, {
      skipOnChange: true,
    });
    this.orderBy.fromURLSearchParam(Router.query);

    if ("search" in Router.query && typeof Router.query.search === "string") {
      this.searchTerm = Router.query.search;
    }

    if (
      "date_from" in Router.query &&
      typeof Router.query.date_from === "string" &&
      "date_to" in Router.query &&
      typeof Router.query.date_to === "string"
    ) {
      this.dateRange = {
        start: new Date(Router.query.date_from),
        end: new Date(Router.query.date_to),
      };
    }

    this.withPrepayments = new BooleanState(
      Router.query.with_prepayments === "true",
    );
    this.pagination.fromURLSearchParam(Router.query);
    this.fetchTableData();
    this.fetchSummaryData();
  }

  @computed
  get searchParamsExtended() {
    return {
      ...commonSearchParams(this),
      date_from: toApiDate(this.dateRange.start),
      date_to: toApiDate(this.dateRange.end),
      with_prepayments: this.withPrepayments.value ? "true" : "false",
    };
  }

  @action.bound
  onSearch(value: string) {
    this.searchTerm = value;
    this.pagination.resetPage();
    this.fetchSummaryData();
  }

  @action.bound
  async setProjectStageOptions(pipedriveStore: PipedriveStore) {
    await pipedriveStore.fetchOptions();
    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.ProjectStageId)
      ?.setOptions(pipedriveStore.projectStageOptions);
  }

  @action.bound
  setDateRange(dateRange: DateRange) {
    this.dateRange = dateRange;
    this.pagination.resetPage();
    this.fetchSummaryData();
  }

  @computed
  get isTableDataLoading(): boolean {
    return this.fetcherTableData.isLoading.value;
  }

  @computed
  get isSummaryLoading(): boolean {
    return this.fetcherSummary.isLoading.value;
  }

  @computed
  get monthOptions(): IOption[] {
    const months = getMonths();

    return months.map((month, index) => ({
      label: month,
      value: index.toString(),
    }));
  }

  @computed
  get yearOptions(): IOption[] {
    const currentYear = now().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

    return years.map((year) => ({
      label: String(year),
      value: year.toString(),
    }));
  }

  @action.bound
  private async fetchTableData() {
    const [data, error] = await this.fetcherTableData.fetch({
      filters: {
        date_from: toApiDate(this.dateRange.start),
        date_to: toApiDate(this.dateRange.end),
        where: this.where.asWhereParam,
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
        with_prepayment: this.withPrepayments.value,
      },
      first: this.pagination.asParams.first,
      page: this.pagination.asParams.page,
    });

    if (error || !data.projectBillings) {
      this.tableData = [];
      return;
    }

    this.tableData = data.projectBillings.data.map((row) => ({
      id: `${row.project.id}-project-group`,
      rowType: "group",
      row,
      __subRows: row.items.map((item) => ({
        id: `${row.project.id}-${item.id}-subrow-item`,
        rowType: "item",
        subrow: item,
        parent: row,
      })),
    }));
    this.pagination.setFromPaginatorInfo(data.projectBillings.paginatorInfo);
  }

  @action.bound
  private async fetchSummaryData() {
    const [data, error] = await this.fetcherSummary.fetch({
      filters: {
        date_from: toApiDate(this.dateRange.start),
        date_to: toApiDate(this.dateRange.end),
        where: this.where.asWhereParam,
        search: this.searchTerm,
        with_prepayment: this.withPrepayments.value,
      },
    });

    if (error || !data.projectBillingsSummary) {
      this.summaryData = null;
      return;
    }

    this.summaryData = data.projectBillingsSummary;
  }

  @action.bound
  handleChangeOrderBy(clauses: {
    column: ProjectBillingsOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!clauses.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clauses.column, order: clauses.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  async fetchFilterOptions() {
    const [data, error] = await this.fetcherFilterOptions.fetch();

    if (error) return;

    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));
    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.TeamId)
      ?.setOptions(mapToOptions.teams(data.teamSimpleMap));
    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.ProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));
    this.expenseCategories = data.expenseBudgetCategories ?? [];
    this.where.filtersByColumn
      .get(ProjectBillingsWhereColumn.ProjectCategoryId)
      ?.setOptions(
        data.projectCategories.map((category) => ({
          value: category.id,
          label: category.name,
        })),
      );
  }

  @action.bound
  toggleWithPrepayments(event: React.ChangeEvent<HTMLInputElement>): void {
    if (event.target.checked) {
      this.withPrepayments.on();
    } else {
      this.withPrepayments.off();
    }
    this.pagination.resetPage();
    this.fetchSummaryData();
  }

  @action.bound
  setExportMonth(month: string) {
    this.exportMonth = Number(month);
  }

  @action.bound
  setExportYear(year: string) {
    this.exportYear = Number(year);
  }

  @computed
  get isExportBasicLoading(): boolean {
    return this.fetcherExportBasic.isLoading.value;
  }

  @action.bound
  async handleExportBasic() {
    const [data, error] = await this.fetcherExportBasic.fetch({
      month: this.exportMonth + 1, // API expects 1-12, not 0-11,
      year: this.exportYear,
    });

    if (error || !data?.exportBillingPlan) return;

    window.open(data.exportBillingPlan, "_blank");
    this.basicExportModalDisclosure.onClose();
  }

  @computed
  get isExportDetailedLoading(): boolean {
    return this.fetcherExportDetailed.isLoading.value;
  }

  @action.bound
  async handleExportDetailed() {
    const [data, error] = await this.fetcherExportDetailed.fetch({
      filters: {
        date_from: toApiDate(this.dateRange.start),
        date_to: toApiDate(this.dateRange.end),
        where: this.where.asWhereParam,
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
        with_prepayment: this.withPrepayments.value,
      },
    });

    if (error || !data?.projectBillingsExport) return;

    // Open export URL in new tab
    window.open(data.projectBillingsExport, "_blank");
  }

  @computed
  get columns(): BodyColumnDef<
    ProjectBillingTableRow,
    ProjectBillingsOrderByColumnEnum
  >[] {
    const columns: BodyColumnDef<
      ProjectBillingTableRow,
      ProjectBillingsOrderByColumnEnum
    >[] = [
      {
        id: "project-manager",
        meta: { label: t`PM` },
        header: () => t`PM`,
        cell: ({ row }) => {
          if (!isGroupRow(row.original)) return null;

          const { projectManager } = row.original.row.project;
          if (!projectManager) return null;

          return (
            <UserTooltip user={projectManager}>
              <Avatar
                src={projectManager.image?.urls.thumbnail}
                name={projectManager.full_name}
                colorScheme={projectManager.profile?.hex_color}
              />
            </UserTooltip>
          );
        },
      },
      {
        id: "brand",
        meta: { label: t`Brand` },
        header: () => t`Brand`,
        cell: ({ row }) => {
          if (!isGroupRow(row.original) || !row.original.row.project.brand) {
            return null;
          }

          return (
            <Tooltip
              label={row.original.row.project.brand.name}
              placement="top"
              shouldWrapChildren
            >
              <ButtonLink
                variant="plain"
                href={{
                  pathname: "/brands/detail",
                  query: { id: row.original.row.project.brand.id },
                }}
                mr="2"
                target="_blank"
              >
                <BrandLogo
                  src={row.original.row.project.brand?.image?.urls?.thumbnail}
                  name={row.original.row.project.brand?.name}
                />
              </ButtonLink>
            </Tooltip>
          );
        },
      },
      {
        id: "title",
        meta: { label: t`Title` },
        header: () => t`Title`,
        orderBy: ProjectBillingsOrderByColumnEnum.ProjectTitle,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            const groupRow = row.original.row;

            return (
              <Tooltip
                label={`${groupRow.project.code} - ${groupRow.project.title}`}
                placement="top"
                shouldWrapChildren
              >
                <Link
                  href={{
                    pathname: "/projects/detail",
                    query: { id: groupRow.project.id },
                  }}
                  target="_blank"
                >
                  <HStack align="center">
                    <Text>{groupRow.project.code}</Text>

                    <Badge
                      colorScheme={
                        groupRow.billing_type === ProjectBillingTypeEnum.None
                          ? "red"
                          : groupRow.billing_type ===
                              ProjectBillingTypeEnum.Automatic
                            ? "cyan"
                            : "gray"
                      }
                      size="sm"
                    >
                      {groupRow.billing_type}
                    </Badge>
                  </HStack>

                  <Text>{groupRow.project.title}</Text>
                </Link>
              </Tooltip>
            );
          }

          return <InvoiceNoRenderer subrow={row.original.subrow} />;
        },
      },
      {
        id: "amount",
        meta: { align: "right", label: t`Amount` },
        header: () => t`Amount`,
        footer: undefined,
        orderBy: ProjectBillingsOrderByColumnEnum.PlannedAmount,
        cell: ({ row }) =>
          isGroupRow(row.original) ? (
            <StackedAmountRenderer
              plannedAmount={row.original.row.planned_amount}
              invoicedAmount={row.original.row.invoiced_amount}
              fontWeight="bold"
            />
          ) : (
            <StackedAmountRenderer
              plannedAmount={row.original.subrow.planned_amount}
              invoicedAmount={row.original.subrow.invoiced_amount}
              fontWeight="bold"
            />
          ),
      },
      {
        id: "our_work",
        meta: { align: "right", label: t`Our work` },
        header: () => t`Our work`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? (
            <StackedAmountRenderer
              plannedAmount={row.original.row.planned_our_work}
              invoicedAmount={row.original.row.invoiced_our_work}
            />
          ) : (
            <StackedAmountRenderer
              plannedAmount={row.original.subrow.planned_our_work}
              invoicedAmount={row.original.subrow.invoiced_our_work}
            />
          ),
      },
    ];

    this.expenseCategories.forEach((category) => {
      columns.push({
        id: `expense_category_${category.id}`,
        meta: { align: "right", label: category.name },
        header: () => category.name,
        cell: ({ row }) => {
          const plannedItem = isGroupRow(row.original)
            ? row.original.row.planned_expense_categories.find(
                (item) => item.category_id === category.id,
              )
            : row.original.subrow.planned_expense_categories.find(
                (item) => item.category_id === category.id,
              );
          const invoicedItem = isGroupRow(row.original)
            ? row.original.row.invoiced_expense_categories.find(
                (item) => item.category_id === category.id,
              )
            : row.original.subrow.invoiced_expense_categories.find(
                (item) => item.category_id === category.id,
              );

          return (
            <StackedAmountRenderer
              plannedAmount={plannedItem?.amount}
              invoicedAmount={invoicedItem?.amount}
            />
          );
        },
      });
    });

    columns.push(
      {
        id: "prepayments",
        meta: { align: "right", label: t`Prepayments` },
        header: () => t`Prepayments`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? (
            <StackedAmountRenderer
              plannedAmount={row.original.row.planned_prepayments}
              invoicedAmount={row.original.row.invoiced_prepayments}
            />
          ) : (
            <StackedAmountRenderer
              plannedAmount={row.original.subrow.planned_prepayments}
              invoicedAmount={row.original.subrow.invoiced_prepayments}
            />
          ),
      },
      {
        id: "invoiced_automatically",
        meta: { align: "right", label: t`Invoiced automatically` },
        header: () => t`Invoiced automatically`,
        orderBy: ProjectBillingsOrderByColumnEnum.AutomaticallyInvoiced,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            isGroupRow(row.original)
              ? row.original.row.automatically_invoiced
              : row.original.subrow.automatically_invoiced,
          ),
      },
      {
        id: "invoiced_manually",
        meta: { align: "right", label: t`Invoiced manually` },
        header: () => t`Invoiced manually`,
        orderBy: ProjectBillingsOrderByColumnEnum.ManuallyInvoiced,
        cell: ({ row }) =>
          currency.formatByWorkspace(
            isGroupRow(row.original)
              ? row.original.row.manually_invoiced
              : row.original.subrow.manually_invoiced,
          ),
      },
      {
        id: "balance",
        meta: { align: "right", label: t`Balance` },
        header: () => t`Balance`,
        orderBy: ProjectBillingsOrderByColumnEnum.Balance,
        cell: ({ row }) => {
          const balance = isGroupRow(row.original)
            ? row.original.row.balance
            : row.original.subrow.balance;

          return (
            <Text textColor={balance >= 0 ? undefined : "red.500"}>
              {currency.formatByWorkspace(balance)}
            </Text>
          );
        },
      },
      {
        id: "issue_date",
        meta: { align: "center", label: t`Issue date` },
        header: () => t`Issue date`,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.subrow.issue_date
              ? formatDate(new Date(row.original.subrow.issue_date))
              : "-",
      },
      {
        id: "date_of_supply",
        meta: { align: "center", label: t`Date of supply` },
        header: () => t`Date of supply`,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.subrow.date_of_supply
              ? formatDate(new Date(row.original.subrow.date_of_supply))
              : "-",
      },
      {
        id: "due_date",
        meta: { align: "center", label: t`Due date` },
        header: () => t`Due date`,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.subrow.due_date
              ? formatDate(new Date(row.original.subrow.due_date))
              : "-",
      },
      {
        id: "type",
        meta: { label: t`Type` },
        header: () => t`Type`,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : getDocumentTypeLabel(row.original.subrow.type),
      },
      {
        id: "invoice_no",
        meta: { label: t`Invoice No` },
        header: () => t`Invoice No`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <InvoiceNoRenderer subrow={row.original.subrow} />
          ),
      },
      {
        id: "paid_at",
        meta: { align: "center", label: t`Paid at` },
        header: () => t`Paid at`,
        cell: ({ row }) =>
          isGroupRow(row.original)
            ? null
            : row.original.subrow.paid_at
              ? formatDate(new Date(row.original.subrow.paid_at))
              : typeof row.original.subrow.paid_amount === "number" &&
                  row.original.subrow.paid_amount > 0
                ? t`Partially paid`
                : t`Unpaid`,
      },
      {
        id: "status",
        meta: { label: t`Status` },
        header: () => t`Status`,
        cell: ({ row }) => {
          if (isGroupRow(row.original)) {
            return null;
          }

          const { label, colorScheme } = getStatusBadgeProps(
            row.original.subrow.status,
          );

          return <Badge colorScheme={colorScheme}>{label}</Badge>;
        },
      },
      {
        id: "delivered",
        meta: { align: "center", label: t`Delivered` },
        header: () => t`Delivered`,
        cell: ({ row }) =>
          isGroupRow(row.original) ? null : (
            <DeliveredStatusRenderer
              delivered={row.original.subrow.delivered}
            />
          ),
      },
      {
        id: "note",
        meta: { label: t`Note` },
        header: () => t`Note`,
        cell: ({ row }) => {
          if (isGroupRow(row.original) || !row.original.subrow.note) {
            return null;
          }

          return (
            <Tooltip
              label={row.original.subrow.note}
              placement="top"
              shouldWrapChildren
            >
              <Text maxW="150px" isTruncated>
                {row.original.subrow.note}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: () => null,
      },
    );

    return columns;
  }
}

function getStatusBadgeProps(status: ProjectBillingItemStatusEnum): {
  label: string;
  colorScheme: string;
} {
  switch (status) {
    case ProjectBillingItemStatusEnum.Created:
      return { label: t`Created`, colorScheme: "green" };
    case ProjectBillingItemStatusEnum.Draft:
      return { label: t`Draft`, colorScheme: "gray" };
    case ProjectBillingItemStatusEnum.Failed:
    case ProjectBillingItemStatusEnum.Deleted:
      return { label: t`Failed`, colorScheme: "red" };
    case ProjectBillingItemStatusEnum.ManuallyCreated:
      return { label: t`Manually created`, colorScheme: "green" };
    case ProjectBillingItemStatusEnum.Pending:
      return { label: t`Pending`, colorScheme: "yellow" };
    case ProjectBillingItemStatusEnum.PrepaymentUsed:
      return { label: t`Prepayment used`, colorScheme: "purple" };
    default:
      return { label: status, colorScheme: "gray" };
  }
}

function getDocumentTypeLabel(
  type?: ProjectBillingDocumentTypeEnum | null,
): string {
  switch (type) {
    case ProjectBillingDocumentTypeEnum.CreditNote:
      return t`Credit note`;
    case ProjectBillingDocumentTypeEnum.OutgoingInvoice:
      return t`Outgoing invoice`;
    case ProjectBillingDocumentTypeEnum.OutgoingProformaInvoice:
      return t`Proforma invoice`;
    case ProjectBillingDocumentTypeEnum.Planned:
      return t`Planned`;
    case ProjectBillingDocumentTypeEnum.Prepayment:
      return t`Prepayment`;
    case ProjectBillingDocumentTypeEnum.RequestForInvoicing:
      return t`Request for invoicing`;
    default:
      return "-";
  }
}
